import {
  ChangeEvent,
  ComponentPropsWithRef,
  useEffect,
  useState,
  forwardRef,
  ForwardedRef,
} from "react";
import { VariantProps, tv } from "tailwind-variants";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons";
import { HexColorPicker } from "react-colorful";

import { P } from "@/components/Typography";

const styles = tv({
  slots: {
    base: "sm:max-w-150 relative flex w-full flex-col gap-10",
    input: "inline h-25",
    inputLabel: "h-15 text-sm font-normal leading-4",
    inputContainer:
      "sm:max-w-150 flex h-[62px] w-full flex-row items-center justify-between space-x-4 rounded border border-black p-[13px] sm:justify-start",
    discContainer: "h-30 w-30 rounded-full border-2 border-gray",
    helperTextContainer: "flex flex-row items-center gap-10 !text-caption",
    colorPickerContainer: "absolute top-[-180px] z-50",
    colorPickerCloseContainer: "fixed bottom-0 left-0 right-0 top-0",
  },
  variants: {
    variant: {
      error: {
        inputContainer: "border-red",
        helperTextContainer: "text-red",
        input: "placeholder-red",
      },
    },
  },
});

interface IColorInputProps extends ComponentPropsWithRef<"input"> {
  label: string;
  helperText?: string;
  testId?: string;
  variant?: VariantProps<typeof styles>["variant"];
}

const ColorPicker = forwardRef(
  (
    { label, variant, helperText, testId, ...rest }: IColorInputProps,
    ref: ForwardedRef<HTMLInputElement | null>,
  ) => {
    const { value, onChange } = rest;

    const {
      base,
      input,
      inputLabel,
      inputContainer,
      discContainer,
      helperTextContainer,
      colorPickerContainer,
      colorPickerCloseContainer,
    } = styles({ variant });

    const [colorCode, setColorCode] = useState<string>(value as string);
    const [openColorPicker, setOpenColorPicker] = useState<boolean>(false);

    const toggleColorPicker = () =>
      setOpenColorPicker(openColorPicker => !openColorPicker);

    const handleCloseColorPicker = () => setOpenColorPicker(false);

    const handleChange = (newColor: string) => {
      setColorCode(newColor);
      if (onChange) {
        onChange({
          target: { value: newColor },
        } as ChangeEvent<HTMLInputElement>);
      }
    };

    useEffect(() => {
      setColorCode(value as string);
    }, [value]);

    const renderHelperText = () =>
      helperText && (
        <P className={helperTextContainer()}>
          <FontAwesomeIcon className="mx-2" icon={faInfoCircle} />
          {helperText}
        </P>
      );

    return (
      <div className={base()}>
        {openColorPicker && (
          <div className={colorPickerContainer()}>
            <div
              className={colorPickerCloseContainer()}
              onClick={handleCloseColorPicker}
            />
            <HexColorPicker color={colorCode} onChange={handleChange} />
          </div>
        )}

        <div className={inputLabel()}>{label}</div>
        <div className={inputContainer()}>
          <div
            style={{ backgroundColor: `${colorCode}` }}
            className={discContainer()}
            onClick={toggleColorPicker}
          />
          <div className="flex">
            <input
              id={rest.id || label}
              type="text"
              size={8}
              maxLength={7}
              className={input()}
              placeholder="#000000"
              value={colorCode}
              ref={ref}
              data-testid={testId}
              {...rest}
            />
          </div>
        </div>
        {renderHelperText()}
      </div>
    );
  },
);

export default ColorPicker;
